/**
 * React Query hook returning every component rule for a given id.
 *
 * Powers the "existing rules" table in the component config page. The
 * hook is deliberately kept thin — it only wires the service call to
 * TanStack Query and delegates caching policy to the default query
 * client. Callers that need derived state (sorted, filtered by effect)
 * memoise on the returned `data` themselves.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { RuleStatus } from '@components/shared/configurable';

import {
  componentRulesService,
  type ComponentRuleDTO,
} from '../../api/componentRulesService';
import { componentRulesKeys } from './queryKeys';

/**
 * Options accepted by `useComponentRulesList`.
 *
 * `status` is forwarded directly to the service call and folded into the
 * query key so separate status filters cache independently.
 */
export interface UseComponentRulesListOptions {
  /**
   * Optional lifecycle filter. Omit for the admin list page, pass
   * `'active'` for the HOC evaluator.
   */
  status?: RuleStatus;
}

/**
 * Fetch the rules bound to a given component id.
 *
 * Returns raw DTOs (snake_case); consumers that need the camelCased
 * shape should import `mapComponentRuleFromDTO` from the service file.
 */
export function useComponentRulesList(
  componentId: string,
  options: UseComponentRulesListOptions = {},
): UseQueryResult<ComponentRuleDTO[]> {
  return useQuery<ComponentRuleDTO[]>({
    queryKey: componentRulesKeys.list(componentId, options.status),
    queryFn: () =>
      componentRulesService.listRules(componentId, { status: options.status }),
    enabled: componentId !== '',
  });
}
